import { TuiButton } from '@taiga-ui/core';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { XAlertService } from '@core/services';
import { AccountService } from '@core/services/account/account.service';
import { WorkflowLogoutGuardService } from '@core/services/workflow/workflow-logout-guard.service';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, EMPTY, map, switchMap, take } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { CoreTranslations, CoreTranslationsPayload } from '@core/types';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { OutkeepPermission } from '@outkeep/shared';
import { AppRoutePaths } from '@core/types/app-route-paths';

@Component({
  selector: 'x-no-permission',
  imports: [TuiButton, TranslatePipe],
  templateUrl: './no-permission.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NoPermissionComponent implements OnInit {
  private readonly accountService = inject(AccountService);
  private readonly workflowLogoutGuard = inject(WorkflowLogoutGuardService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly xAlertService = inject(XAlertService);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  private readonly permissionService = inject(OutkeepPermissionService);
  private readonly router = inject(Router);

  public userExists = false;

  translates: Partial<CoreTranslationsPayload> = {
    [CoreTranslations.NoAccessToOutkeepApp]: 'Нет доступа к приложению',
  };

  public ngOnInit(): void {
    this.outkeepTranslateService
      .translate(Object.values(CoreTranslations))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(translates => (this.translates = translates));

    this.accountService.currentUser$
      .pipe(
        distinctUntilChanged((prev, next) => prev?.id === next?.id),
        switchMap(user => {
          if (!user) {
            this.userExists = false;

            return EMPTY;
          }

          this.userExists = true;

          return this.permissionService.canLogistUseBetaOutkeep$;
        }),
        switchMap(canUseApp => {
          if (!canUseApp) {
            this.xAlertService.error(
              this.translates[CoreTranslations.NoAccessToOutkeepApp]!,
            );

            return EMPTY;
          }

          return this.permissionService
            .hasPermission$(OutkeepPermission.LogistOn)
            .pipe(
              take(1),
              map(canUseRequests =>
                canUseRequests
                  ? this.router.createUrlTree([AppRoutePaths.AllRequests])
                  : null,
              ),
            );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(fallbackTree => {
        if (fallbackTree) {
          void this.router.navigateByUrl(fallbackTree);
        }
      });
  }

  logout() {
    void this.workflowLogoutGuard.logout();
  }
}
